import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import API from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';
import { useAuth } from '../contexts/AuthContext';
import SimilarCasesList from './SimilarCasesList';
import {
  XMarkIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  DocumentDuplicateIcon,
  ArrowPathIcon,
  PlayIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

// --- Types ---

interface MatchedCase {
  id: string;
  caseNumber: string;
  description: string;
  country: string;
  region?: string;
  violationTypes: string[];
  caseStartDate?: string;
  status: string;
}

interface DuplicateMatch {
  id: string;
  caseId: string;
  matchedCaseId: string;
  dedupRunId?: string;
  similarityScore: number;
  llmConfidence: number;
  llmReasoning: string;
  status: 'PENDING_REVIEW' | 'CONFIRMED' | 'DISMISSED' | 'SUPERSEDED';
  autoRejected: boolean;
  reviewedBy?: { id: string; firstName: string; lastName: string } | null;
  reviewedAt?: string | null;
  matchedCase: MatchedCase;
  createdAt: string;
}

export interface DedupRun {
  id: string;
  caseId: string;
  triggeredById: string | null;
  status: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED';
  matchCount: number;
  error?: string;
  startedAt?: string;
  completedAt?: string;
  createdAt: string;
  triggeredBy?: { id: string; firstName: string; lastName: string } | null;
  matches: DuplicateMatch[];
}

interface DeduplicationPanelProps {
  caseId: string;
  /** Reports match counts up to the parent (e.g. for a page-level possible-duplicate banner). */
  onSummary?: (summary: { pending: number; confirmed: number; runs: DedupRun[] }) => void;
  /** Called after a confirm or dismiss changed the case (status, merge record), so the parent can
   *  refetch its own copy — CaseDetail holds caseData in local state and would otherwise show the
   *  old status until a reload. */
  onChange?: () => void;
  /** The checker itself — runs, matches, the run button. Shown to every reviewer role; the Run check
   *  button inside it is a paid AI operation and stays ADMIN/APPROVER. Without it no runs are fetched
   *  or reported and the card is just the similar-cases list. */
  showChecker?: boolean;
  /** The "Most similar cases" list in the same card, under the checker. Off on archived cases. */
  showSimilar?: boolean;
  /** Changes when the case is edited, so the similar list refetches. */
  similarRefreshKey?: string;
}

// --- Config ---

const matchStatusConfig: Record<DuplicateMatch['status'], { label: string; color: string; icon: React.ElementType }> = {
  PENDING_REVIEW: { label: 'Pending Review', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300', icon: ExclamationTriangleIcon },
  CONFIRMED: { label: 'Confirmed Duplicate', color: 'bg-red-100 text-red-700', icon: XCircleIcon },
  DISMISSED: { label: 'Dismissed', color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200', icon: CheckCircleIcon },
  SUPERSEDED: { label: 'Superseded', color: 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400', icon: ClockIcon },
};

const runStatusConfig: Record<DedupRun['status'], { label: string; color: string }> = {
  PENDING: { label: 'Pending', color: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400' },
  RUNNING: { label: 'Running', color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200' },
  COMPLETED: { label: 'Completed', color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200' },
  FAILED: { label: 'Failed', color: 'bg-red-100 text-red-700' },
};

// --- Component ---

export default function DeduplicationPanel({ caseId, onSummary, onChange, showChecker = true, showSimilar = true, similarRefreshKey }: DeduplicationPanelProps) {
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [runs, setRuns] = useState<DedupRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [updating, setUpdating] = useState<string | null>(null);
  const [triggering, setTriggering] = useState(false);

  // Deciding a match (Confirm / Dismiss) is open to every reviewer role since 2026-11-04; starting a
  // run is a paid AI operation and stays with ADMIN/APPROVER.
  const canReview = user?.role === 'ADMIN' || user?.role === 'APPROVER' || user?.role === 'RESEARCHER';
  const canRun = user?.role === 'ADMIN' || user?.role === 'APPROVER';

  const fetchRuns = useCallback(async () => {
    try {
      const response = await API.get(`/api/dedup/runs/${caseId}`);
      setRuns(response.data);
    } catch {
      setRuns([]);
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    if (!showChecker) { setLoading(false); return; }
    fetchRuns();
  }, [fetchRuns, showChecker]);

  // Poll while any run is PENDING or RUNNING
  useEffect(() => {
    const hasActive = runs.some(r => r.status === 'PENDING' || r.status === 'RUNNING');
    if (!hasActive) return;
    const interval = setInterval(fetchRuns, 5000);
    return () => clearInterval(interval);
  }, [runs, fetchRuns]);

  const handleTriggerRun = async () => {
    if (triggering) return; // guard against double-trigger while the request is in flight
    const ok = await confirm({
      title: 'Run Case Duplicate Checker?',
      message: 'This runs an AI check that uses tokens (billable). Only run it when needed. Proceed?',
      confirmLabel: 'Proceed',
      cancelLabel: 'Cancel',
    });
    if (!ok) return;
    setTriggering(true);
    try {
      await API.post(`/api/dedup/runs/${caseId}`);
      showToast('success', 'Check started');
      fetchRuns();
    } catch {
      showToast('error', 'Failed to start check');
    } finally {
      setTriggering(false);
    }
  };

  const handleReview = async (matchId: string, status: 'CONFIRMED' | 'DISMISSED') => {
    setUpdating(matchId);
    try {
      const res = await API.patch(`/api/dedup/matches/${matchId}`, { status });
      // The backend archives first and enriches the canonical best-effort; `enriched: false` means the
      // archive happened but no proposed updates were written (e.g. no VALIDATOR_URL on this stack).
      const enriched = res.data?.enriched;
      showToast(
        status === 'CONFIRMED' && enriched === false ? 'error' : 'success',
        status === 'CONFIRMED'
          ? (enriched === false ? 'Match confirmed — case archived, but the original could not be enriched (validator unreachable)' : 'Match confirmed — case archived')
          : 'Match dismissed',
      );
      fetchRuns();
      onChange?.();
    } catch {
      showToast('error', 'Failed to update match');
    } finally {
      setUpdating(null);
    }
  };

  // Derived state
  const allMatches = runs.flatMap(r => r.matches);
  const activeMatches = allMatches.filter(m => m.status !== 'SUPERSEDED');
  const pendingCount = activeMatches.filter(m => m.status === 'PENDING_REVIEW').length;
  const confirmedCount = activeMatches.filter(m => m.status === 'CONFIRMED').length;
  const hasActiveRun = runs.some(r => r.status === 'PENDING' || r.status === 'RUNNING');
  const hasFailedRun = runs.some(r => r.status === 'FAILED');
  // The match the card previews and "View details" opens: a pending one first, else the latest.
  const topMatch = activeMatches.find(m => m.status === 'PENDING_REVIEW') || activeMatches[0];

  // Report counts up to the parent (page-level banner) whenever they change.
  useEffect(() => {
    if (!showChecker) return;
    onSummary?.({ pending: pendingCount, confirmed: confirmedCount, runs });
  }, [pendingCount, confirmedCount, runs, onSummary, showChecker]);

  if (!showChecker && !showSimilar) return null;

  const checker = loading ? (
    <div>
      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Case Duplicate Checker</h3>
      <div className="animate-pulse space-y-2">
        <div className="h-5 bg-gray-200 dark:bg-gray-600 rounded w-1/2" />
        <div className="h-2 bg-gray-200 dark:bg-gray-600 rounded" />
      </div>
    </div>
  ) : (
    <div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <DocumentDuplicateIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Case Duplicate Checker</h3>
          </div>
          {hasActiveRun ? (
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
              <ArrowPathIcon className="h-3 w-3 animate-spin" />
              Running
            </span>
          ) : confirmedCount > 0 ? (
            <span className="inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700">
              Duplicate Found
            </span>
          ) : pendingCount > 0 ? (
            <span className="inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
              {pendingCount} Pending Review
            </span>
          ) : hasFailedRun ? (
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700">
              <ExclamationTriangleIcon className="h-3 w-3" />
              Run Failed
            </span>
          ) : runs.length > 0 ? (
            <span className="inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
              No Duplicates
            </span>
          ) : null}
        </div>

        {runs.length === 0 ? (
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">No dedup runs yet</p>
        ) : (
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
            {runs.length} run{runs.length !== 1 ? 's' : ''} &middot; {activeMatches.length} match{activeMatches.length !== 1 ? 'es' : ''}
          </p>
        )}

        {/* Top pending match preview */}
        {topMatch && (() => {
          const top = topMatch;
          return (
            <div className="border border-gray-200 dark:border-gray-700 rounded-md p-3 mb-3">
              <div className="flex items-center justify-between mb-1">
                <Link to={`/cases/${top.matchedCase.id}`} className="text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800">
                  {top.matchedCase.caseNumber}
                </Link>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {top.llmConfidence}% confidence
                </span>
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">{top.matchedCase.description}</p>
              <div className="mt-2 flex items-center gap-2">
                <ConfidenceBar value={top.llmConfidence} />
                <MatchStatusBadge status={top.status} />
              </div>
            </div>
          );
        })()}

        <div className="flex items-center gap-2">
          {/* With a match, details means the merge view (the only place a duplicate decision is
              made); it comes back here afterwards. With runs but no match there is nothing to
              compare, so the run list opens instead — that is where a failed run's error is. */}
          {(runs.length > 0 || activeMatches.length > 0) && (
            <button
              onClick={() => (topMatch ? navigate(`/merge/${topMatch.id}`, { state: { returnTo: `/cases/${caseId}` } }) : setModalOpen(true))}
              className="flex-1 text-center text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 font-medium py-1"
            >
              View details
            </button>
          )}
          {canRun && !hasActiveRun && (
            <button
              onClick={handleTriggerRun}
              disabled={triggering}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-indigo-600 dark:text-indigo-400 border border-indigo-300 dark:border-indigo-700 rounded-md hover:bg-indigo-50 disabled:opacity-50"
            >
              <PlayIcon className="h-3 w-3" />
              {triggering ? 'Starting...' : 'Run check'}
            </button>
          )}
        </div>
    </div>
  );

  return (
    <>
      {/* One card: the checker (what the engine decided or is deciding) and, under a rule, the
          most similar cases (what it ranks closest, for the reviewer to check). */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        {showChecker && checker}
        {showSimilar && (
          <div className={showChecker ? 'mt-5 border-t border-gray-200 dark:border-gray-700 pt-4' : ''}>
            <SimilarCasesList caseId={caseId} refreshKey={similarRefreshKey} />
          </div>
        )}
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center px-4">
            <div className="fixed inset-0 bg-black bg-opacity-30" onClick={() => setModalOpen(false)} />
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[85vh] flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Case Duplicate Checker Results</h2>
                <button onClick={() => setModalOpen(false)} className="text-gray-400 dark:text-gray-500 hover:text-gray-600">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              {/* Body */}
              <div className="overflow-y-auto px-6 py-4 space-y-6">
                {runs.map(run => (
                  <RunSection
                    key={run.id}
                    run={run}
                    canReview={canReview}
                    updating={updating}
                    onReview={handleReview}
                    onCloseModal={() => setModalOpen(false)}
                  />
                ))}
                {runs.length === 0 && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">No dedup runs found for this case.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// --- Run Section ---

function RunSection({
  run,
  canReview,
  updating,
  onReview,
  onCloseModal,
}: {
  run: DedupRun;
  canReview: boolean;
  updating: string | null;
  onReview: (matchId: string, status: 'CONFIRMED' | 'DISMISSED') => void;
  onCloseModal: () => void;
}) {
  const rcfg = runStatusConfig[run.status];
  const isAuto = !run.triggeredById;

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
      {/* Run header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-900 rounded-t-lg border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${rcfg.color}`}>
            {run.status === 'RUNNING' && <ArrowPathIcon className="h-3 w-3 animate-spin mr-1" />}
            {rcfg.label}
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {isAuto ? 'Auto' : `Manual — ${run.triggeredBy?.firstName} ${run.triggeredBy?.lastName}`}
          </span>
          <span className="text-xs text-gray-400 dark:text-gray-500">
            {new Date(run.createdAt).toLocaleString()}
          </span>
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400">
          {run.matchCount} match{run.matchCount !== 1 ? 'es' : ''}
          {run.completedAt && (
            <span className="ml-2 text-gray-400 dark:text-gray-500">
              completed {new Date(run.completedAt).toLocaleString()}
            </span>
          )}
        </div>
      </div>

      {/* Run error */}
      {run.status === 'FAILED' && run.error && (
        <div className="px-4 py-2 bg-red-50 text-xs text-red-700 border-b border-red-100">
          Error: {run.error}
        </div>
      )}

      {/* Matches */}
      {run.matches.length > 0 ? (
        <div className="divide-y divide-gray-100">
          {run.matches.map(match => (
            <MatchCard
              key={match.id}
              match={match}
              canReview={canReview}
              updating={updating}
              onReview={onReview}
              onCloseModal={onCloseModal}
            />
          ))}
        </div>
      ) : (
        <div className="px-4 py-4 text-xs text-gray-500 dark:text-gray-400 text-center">
          {run.status === 'COMPLETED' ? 'No duplicates found' :
           run.status === 'RUNNING' || run.status === 'PENDING' ? 'Waiting for results...' : 'No matches'}
        </div>
      )}
    </div>
  );
}

// --- Match Card ---

function MatchCard({
  match,
  canReview,
  updating,
  onReview,
  onCloseModal,
}: {
  match: DuplicateMatch;
  canReview: boolean;
  updating: string | null;
  onReview: (matchId: string, status: 'CONFIRMED' | 'DISMISSED') => void;
  onCloseModal: () => void;
}) {
  const cfg = matchStatusConfig[match.status];
  const Icon = cfg.icon;
  const isSuperseded = match.status === 'SUPERSEDED';

  return (
    <div className={`p-4 ${isSuperseded ? 'opacity-50' : ''}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Link
            to={`/cases/${match.matchedCase.id}`}
            className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-800"
            onClick={onCloseModal}
          >
            {match.matchedCase.caseNumber}
          </Link>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${cfg.color}`}>
            <Icon className="h-3 w-3" />
            {cfg.label}
          </span>
          {match.autoRejected && (
            <span className="text-xs text-gray-400 dark:text-gray-500">(auto-rejected)</span>
          )}
        </div>
        <div className="text-right flex-shrink-0 ml-3">
          <div className="text-xs text-gray-500 dark:text-gray-400">Similarity: {Math.round(match.similarityScore * 100)}%</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">LLM Confidence: {match.llmConfidence}%</div>
        </div>
      </div>

      {/* Matched case info */}
      <div className="grid grid-cols-2 gap-2 mb-2 text-xs">
        <div>
          <span className="font-medium text-gray-500 dark:text-gray-400">Country:</span>{' '}
          <span className="text-gray-900 dark:text-gray-100">{match.matchedCase.country}</span>
          {match.matchedCase.region && <span className="text-gray-500 dark:text-gray-400"> / {match.matchedCase.region}</span>}
        </div>
        <div>
          <span className="font-medium text-gray-500 dark:text-gray-400">Status:</span>{' '}
          <span className="text-gray-900 dark:text-gray-100">{match.matchedCase.status}</span>
        </div>
        <div className="col-span-2">
          <span className="font-medium text-gray-500 dark:text-gray-400">Violations:</span>{' '}
          <span className="text-gray-900 dark:text-gray-100">{match.matchedCase.violationTypes.join(', ')}</span>
        </div>
        {match.matchedCase.caseStartDate && (
          <div>
            <span className="font-medium text-gray-500 dark:text-gray-400">Case Start:</span>{' '}
            <span className="text-gray-900 dark:text-gray-100">{new Date(match.matchedCase.caseStartDate).toLocaleDateString()}</span>
          </div>
        )}
      </div>

      {/* Description */}
      <p className="text-xs text-gray-700 dark:text-gray-300 mb-2 line-clamp-3">{match.matchedCase.description}</p>

      {/* LLM Reasoning */}
      {match.llmReasoning && (
        <div className="bg-gray-50 dark:bg-gray-900 rounded-md p-3 mb-2">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">AI Reasoning</p>
          <p className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{match.llmReasoning}</p>
        </div>
      )}

      {/* Confidence bar */}
      <div className="mb-2">
        <ConfidenceBar value={match.llmConfidence} size="lg" />
      </div>

      {/* Review actions */}
      {match.status === 'PENDING_REVIEW' && canReview && (
        <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
          <button
            onClick={() => onReview(match.id, 'CONFIRMED')}
            disabled={updating === match.id}
            className="flex-1 px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50"
          >
            {updating === match.id ? 'Updating...' : 'Confirm Duplicate'}
          </button>
          <button
            onClick={() => onReview(match.id, 'DISMISSED')}
            disabled={updating === match.id}
            className="flex-1 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 rounded-md disabled:opacity-50"
          >
            {updating === match.id ? 'Updating...' : 'Dismiss'}
          </button>
        </div>
      )}

      {/* Review info */}
      {match.reviewedBy && match.reviewedAt && (
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
          Reviewed by {match.reviewedBy.firstName} {match.reviewedBy.lastName} on {new Date(match.reviewedAt).toLocaleDateString()}
        </p>
      )}
    </div>
  );
}

// --- Helpers ---

function ConfidenceBar({ value, size = 'sm' }: { value: number; size?: 'sm' | 'lg' }) {
  const color = value >= 80 ? 'bg-red-500' : value >= 50 ? 'bg-yellow-500' : 'bg-green-500';
  return (
    <div className="flex items-center gap-2 flex-1">
      <div className={`bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden ${size === 'lg' ? 'w-full h-2.5' : 'flex-1 h-2'}`}>
        <div className={`h-full ${color} rounded-full`} style={{ width: `${value}%` }} />
      </div>
      <span className={`font-medium whitespace-nowrap ${size === 'lg' ? 'text-xs text-gray-600 dark:text-gray-400' : 'text-xs text-gray-500 dark:text-gray-400'}`}>
        {value}%
      </span>
    </div>
  );
}

function MatchStatusBadge({ status }: { status: DuplicateMatch['status'] }) {
  const cfg = matchStatusConfig[status];
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${cfg.color}`}>
      <Icon className="h-3 w-3" />
      {cfg.label}
    </span>
  );
}
